import { useOutletContext } from 'react-router-dom';
import { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TaskCard from '../../../../components/TaskCard/TaskCard';
import {
  addCommentWithAttachment,
  changeTaskInfo,
  createTask,
  downloadFile,
  fetchAllSystemUsers,
  fetchPagedTasks,
  fetchTaskPriorities,
  fetchTaskStates,
  fetchTaskTitles,
  getAllTaskByIds,
} from '../../../../services/TaskService';
import ManageTaskModal from '../../../Tasks/ManageTaskModal';
import EditTaskQuickModal from './EditTaskQuickModal';
import { getCurrentUser } from '../../../../services/authService';
import { useTaskHtml } from 'hooks/task/useTaskHtml';
import { Editor } from '@tinymce/tinymce-react';
import { TinyMCE_ApiKey } from 'config';
import { useUpdateTaskHtml } from 'hooks/task/useUpdateTaskHtml';
import { useAssignDocumentToTask } from 'hooks/task/useAssignDocumentToTask';
import { usePublishTaskDocument } from 'hooks/task/usePublishTaskDocument';
import FloatingAlert from 'components/Alert/FloatingAlert';
import { uploadCommentTaskDoc } from 'services/commentTaskDocService';
import useViewportHeight from 'utils/useViewportHeight';
import ApprovalFlowModal from '../../../ApprovalRequest/ApprovalFlowModal';
import {
  WORKFLOW_STATUS,
  getWorkflowInstance,
} from '../../../../services/workflowService';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { configTasksI18n } from './i18n';
import { useSnackbar } from 'context/SnackbarContext';
import usePermissionsCauses from 'permissions/modules/causes/usePermissionsCauses';
import { getPendingProcessByTask } from 'services/adminNegotiationService';
import AdminNegotiationProcessModal from '../../../AdminNegotiation/AdminNegotiationProcessModal';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';


const parseWorkflowStatus = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toLowerCase();
  if (normalized === 'inprogress') return WORKFLOW_STATUS.IN_PROGRESS;
  if (normalized === 'approved') return WORKFLOW_STATUS.APPROVED;
  if (normalized === 'rejected') return WORKFLOW_STATUS.REJECTED;
  if (normalized === 'finalized') return WORKFLOW_STATUS.FINALIZED;

  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
};

const formatWorkflowDate = (isoDate) => {
  if (!isoDate) return '-';

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '-';

  return `${String(date.getDate()).padStart(2, '0')}/${String(
    date.getMonth() + 1
  ).padStart(2, '0')}/${date.getFullYear()}`;
};
const CaseTasks = () => {
  const { language } = useLanguage();
  const configTasksLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configTasksI18n });
  const { showSnackbar } = useSnackbar();
  const { DesignSystemComponents, caseData } = useOutletContext();
  const { Button, Dropdown, Input, Modal } = DesignSystemComponents;

  const location = useLocation();
  const navigate = useNavigate();
  const taskIdToOpen = location.state?.taskIdToOpen || null;
  const taskRefs = useRef({});

  const [userId, setUserId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [allUserOptions, setAllUserOptions] = useState([]);
  const [taskTitleOptions, setTaskTitleOptions] = useState([]);
  const [rawEstadoOptions, setRawEstadoOptions] = useState([]);
  const [prioridadOptions, setPrioridadOptions] = useState([]);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [showQuickModal, setShowQuickModal] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskToQuickEdit, setTaskToQuickEdit] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [selectedTaskForAttachment, setSelectedTaskForAttachment] =
    useState(null);
  const editorRef = useRef(null);
  const [showSendWarning, setShowSendWarning] = useState(false);
  const [isLoadingTaskApproved, setIsLoadingTaskApproved] = useState(false);
  const [taskPendingApproval, setTaskPendingApproval] = useState(null);
  const [errorTaskWithDocumentFlow, setErrorTaskWithDocumentFlow] =
    useState(null);
  const [workflowByTaskId, setWorkflowByTaskId] = useState({});
  const [selectedWorkflowFlow, setSelectedWorkflowFlow] = useState(null);
  const [selectedWorkflowRequest, setSelectedWorkflowRequest] = useState(null);
  const [isWorkflowFlowLoading, setIsWorkflowFlowLoading] = useState(false);
  const [workflowError, setWorkflowError] = useState(null);
  // Intercepción NA: si el caso tiene un proceso viability_pending, aprobar/rechazar la
  // tarea abre primero el modal de decisión de viabilidad; recién al confirmar se aplica
  // el cambio de estado de la tarea. { task, selected, checkId, clientLabel }.
  const [naIntercept, setNaIntercept] = useState(null);
  // Aviso 409 del guard server-side (proceso NA que debe resolverse desde Documentos):
  // { message, checkId }. Abre un modal con el mensaje del back y el deep-link "Ver proceso".
  const [naConflict, setNaConflict] = useState(null);
  const isOld = false;

  const {
    canCreateTask,
    canManageTask,
    canEditTask,
    canPublishComment,
    canAttachFile,
  } = usePermissionsCauses();

  const { html, setHtml, isLoadingHtml, htmlError, getTaskHtml } =
    useTaskHtml();

  const { updateHtml, isUpdatingHtml } = useUpdateTaskHtml();
  const { assignDocumentToTask } = useAssignDocumentToTask();
  const { publish } = usePublishTaskDocument();

  useEffect(() => {
    configTasksI18n.changeLanguage(configTasksLanguage);
  }, [configTasksLanguage]);

  const handleOpenAttachment = async (task) => {
    setSelectedTaskForAttachment(task);
    setShowAttachmentModal(true);
    await getTaskHtml(task.id);
  };

  const handleOpenCommentDocument = async (doc) => {
    const payload = {
      tipoComentario: 1,
      commentableId: doc.id,
      urlAttachment: doc.urlAttachment || doc.urlAdjunto,
      nameAttachment: doc.nameAttachment || doc.nombreAdjunto,
    };
    try {
      await downloadFile(payload);
    } catch (e) {
      console.error('Error al descargar archivo: ', e);
      showSnackbar(t('s3_download_error'), 'error');
    }
  };

  const editTaskHtmlContent = async () => {
    if (!selectedTaskForAttachment?.id || !html) return;

    try {
      await updateHtml(selectedTaskForAttachment.id, html);
      setShowAttachmentModal(false);
      setSelectedTaskForAttachment(null);
      setHtml('');
    } catch (error) {
      console.error('Error al actualizar HTML de la tarea:', error);
    }
  };

  const hasDocumentAttached = (task) => {
    return (
      !!task?.documentConfigurationId || !!task?.nameFile || !!task?.fileName
    );
  };

  // Hacía una petición HTTP por tarea (con pageSize 2000, hasta 2.000 en paralelo) para
  // pintar un badge de workflow, y bloqueaba el spinner hasta terminar. Ver el mismo
  // caso en useTaskWorkflowFlow, del que esta función es un duplicado.
  const loadWorkflowsForTasks = async () => {
    setWorkflowByTaskId({});
  };

  const handleViewWorkflow = async (taskItem) => {
    const taskId = taskItem?.id;
    if (!taskId) return;

    const workflowSummary = workflowByTaskId[taskId];
    if (!workflowSummary?.id) return;

    setIsWorkflowFlowLoading(true);
    setWorkflowError(null);

    try {
      const detail = await getWorkflowInstance(workflowSummary.id);
      const taskPriority =
        typeof (taskItem?.priority || taskItem?.prioridad) === 'string' &&
        (taskItem?.priority || taskItem?.prioridad).trim()
          ? taskItem.priority || taskItem.prioridad
          : 'Media';

      setSelectedWorkflowFlow(detail);
      setSelectedWorkflowRequest({
        task: taskItem?.titulo || taskItem?.title || `Tarea #${taskId}`,
        workflow: detail?.workflowName || workflowSummary?.workflowName || '-',
        requester:
          detail?.initiatedByName ||
          (detail?.initiatedByUserId
            ? `Usuario #${detail.initiatedByUserId}`
            : '-'),
        date: formatWorkflowDate(detail?.startedAtUtc),
        priority: taskPriority,
      });
    } catch (workflowLoadError) {
      console.error('Error al cargar detalle del workflow:', workflowLoadError);
      setWorkflowError(
        'No fue posible cargar el detalle del workflow para esta tarea.'
      );
      setSelectedWorkflowFlow(null);
      setSelectedWorkflowRequest(null);
    } finally {
      setIsWorkflowFlowLoading(false);
    }
  };

  const fetchTasksDetails = async (tasksIds) => {
    try {
      setLoadingTasks(true);
      const resp = await getAllTaskByIds(tasksIds, isOld);
      const items = Array.isArray(resp) ? resp : [];
      setTasks(items);
      await loadWorkflowsForTasks(items);
    } catch (error) {
      console.error(error);
      setTasks([]);
      setWorkflowByTaskId({});
    } finally {
      setLoadingTasks(false);
    }
  };

  const fetchTasks = async () => {
    try {
      setLoadingTasks(true);
      if (!caseData?.id) return;
      const queryParams = { casoId: caseData.id, pageSize: 2000 };
      const resp = await fetchPagedTasks(queryParams);
      const taskIds = resp.items.filter((t) => t.id).map((t) => t.id);
      await fetchTasksDetails(taskIds);
    } catch (error) {
      console.error('Error al obtener Tareas:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const fetchNewTaskOptions = async () => {
    try {
      const [states, priorities, allUsers, titles] = await Promise.all([
        fetchTaskStates(),
        fetchTaskPriorities(),
        fetchAllSystemUsers(),
        fetchTaskTitles(),
      ]);

      setRawEstadoOptions(Array.isArray(states) ? states : []);
      setPrioridadOptions(priorities);
      setAllUserOptions(allUsers);
      setTaskTitleOptions(titles);
    } catch (error) {
      console.error('Error loading options:', error);
    }
  };

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    } else {
      console.warn('No se pudo obtener el usuario autenticado');
    }
  }, []);

  useEffect(() => {
    if (!userId) return;

    const fetchData = async () => {
      await fetchTasks();
      await fetchNewTaskOptions();
    };
    fetchData();
  }, [caseData?.id, userId]);

  const handleAddTask = async (taskDataPayload) => {
    try {
      await createTask(taskDataPayload);
      setShowModal(false);
      await fetchTasks();
    } catch (error) {
      console.error('Error al crear la tarea:', error);
    }
  };

  const localizedEstadoOptions = useMemo(() => {
    const statusLabels = {
      approved: t('configTasks_badge_approved'),
      in_progress: t('configTasks_badge_inProgress'),
      rejected: t('configTasks_badge_rejected'),
      unassigned: t('configTasks_badge_unassigned'),
    };

    return (Array.isArray(rawEstadoOptions) ? rawEstadoOptions : []).map(
      (statusOption) => ({
        ...statusOption,
        label:
          statusLabels[statusOption.label] ||
          statusOption.name ||
          statusOption.label,
      })
    );
  }, [rawEstadoOptions, t]);

  function getStatusLabel(status, statusOptions) {
    if (!statusOptions || !Array.isArray(statusOptions)) {
      return t('configTasks_badge_noStatus');
    }

    const statusStr = String(status);

    const matchByValue = statusOptions.find(
      (opt) =>
        String(opt.value) === statusStr ||
        String(opt.id) === statusStr ||
        String(opt.backendId) === statusStr
    );

    if (matchByValue?.label) return matchByValue.label;

    const normalized = statusStr.trim().toLowerCase();
    if (['approved', 'aprobada'].includes(normalized)) {
      return t('configTasks_badge_approved');
    }
    if (['in_progress', 'en progreso'].includes(normalized)) {
      return t('configTasks_badge_inProgress');
    }
    if (['rejected', 'rechazada'].includes(normalized)) {
      return t('configTasks_badge_rejected');
    }
    if (['unassigned', 'sin asignar'].includes(normalized)) {
      return t('configTasks_badge_unassigned');
    }

    return t('configTasks_badge_noStatus');
  }

  // Ejecuta un cambio de estado de tarea (changeTaskInfo). Si el back responde 409 con
  // checkId (guard NA: proceso que debe resolverse desde Documentos), muestra el banner
  // con deep-link "Revisar tarea" y devuelve false (sin re-lanzar). El resto de errores
  // se re-lanzan al caller. Wrapper común de los 4 sitios que cambian estado de tarea.
  const runChangeTaskInfo = async (...args) => {
    setNaConflict(null);
    try {
      await changeTaskInfo(...args);
      return true;
    } catch (error) {
      const checkId = error?.response?.data?.checkId;
      if (error?.response?.status === 409 && checkId) {
        setNaConflict({ message: error.response.data.message, checkId });
        return false;
      }
      throw error;
    }
  };

  const handleChangeTaskState = async (selected, task) => {
    try {
      const newStatusLabel = getStatusLabel(
        selected.status,
        localizedEstadoOptions
      );

      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? {
                ...t,
                estado: newStatusLabel,
                estadoId: String(selected.status),
              }
            : t
        )
      );

      await runChangeTaskInfo(
        task.id,
        selected.status,
        userId,
        selected.dueDate,
        selected.assignedUserId,
        selected.createAt,
        task.creadorNombre
      );

      await fetchTasks();

      setShowQuickModal(false);
    } catch (error) {
      console.error('Error actualizando estado de la tarea:', error);
    }
  };

  // Aplica el cambio de estado de la tarea (flujo original, sin intercepción).
  const applyTaskDecision = async (selected, task) => {
    try {
      const newStatusLabel = selected ? 'approved' : 'rejected';
      const newStatusId = selected ? '1' : '4';

      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? {
                ...t,
                estado: newStatusLabel,
                estadoId: newStatusId,
                status: newStatusLabel,
              }
            : t
        )
      );

      await runChangeTaskInfo(task.id, newStatusId, userId);

      await fetchTasks();
    } catch (error) {
      console.error('Error actualizando estado de la tarea:', error);
    }
  };

  const handleApproveOrReject = async (selected, task) => {
    setNaConflict(null);
    // Intercepción NA por tarea (no por caso): solo si la tarea es el trigger de un
    // proceso viability_pending. Evita interceptar tareas ajenas del mismo caso.
    if (task?.id) {
      try {
        const pending = await getPendingProcessByTask(task.id);
        const checkId =
          pending?.id ?? pending?.checkId ?? pending?.viabilityCheckId;
        if (pending && checkId) {
          setNaIntercept({
            task,
            selected,
            checkId,
            clientLabel: pending.contactIdentifier,
          });
          return; // se difiere hasta que se resuelva el modal
        }
      } catch (error) {
        // Si la consulta falla, seguimos con el flujo normal de la tarea.
        console.error('Error consultando proceso NA pendiente:', error);
      }
    }
    await applyTaskDecision(selected, task);
  };

  const handlePostComment = async (taskId, text, files = []) => {
    if (!text || !taskId || !userId) return;
    try {
      // Crear el comentario (SIN archivo)
      const resp = await addCommentWithAttachment(taskId, text, userId, null);

      // Subir N archivos si existen
      if (files.length > 0 && resp) {
        await uploadCommentTaskDoc({
          commentTaskId: resp,
          creatorId: userId,
          caseId: resp.caseId,
          files,
        });
      }
      await fetchTasks();
    } catch (error) {
      console.error('Error al publicar comentario:', error);
    }
  };

  const statusOptions = useMemo(
    () => [
      { label: t('configTasks_filter_all'), value: 'todos' },
      { label: t('configTasks_filter_inProgress'), value: 'en progreso' },
      { label: t('configTasks_filter_approved'), value: 'aprobada' },
      { label: t('configTasks_filter_rejected'), value: 'rechazada' },
      { label: t('configTasks_filter_unassigned'), value: 'sin asignar' },
    ],
    [t]
  );

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredTasks = (tasks || [])
    .filter((task) => {
      const title = (task.titulo || task.title || '').toString().toLowerCase();
      const description = (task.descripcion || task.description || '')
        .toString()
        .toLowerCase();

      const matchesSearch =
        !normalizedQuery ||
        title.includes(normalizedQuery) ||
        description.includes(normalizedQuery);

      const rawEstado = (task.estado || '').toLowerCase();
      let canonicalEstado = '';

      if (['in_progress', 'en progreso'].includes(rawEstado)) {
        canonicalEstado = 'en progreso';
      } else if (['approved', 'aprobada'].includes(rawEstado)) {
        canonicalEstado = 'aprobada';
      } else if (['rejected', 'rechazada'].includes(rawEstado)) {
        canonicalEstado = 'rechazada';
      } else if (['unassigned', 'sin asignar'].includes(rawEstado)) {
        canonicalEstado = 'sin asignar';
      } else {
        canonicalEstado = rawEstado;
      }

      const matchesStatus =
        statusFilter === 'todos' || canonicalEstado === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .slice()
    .sort((a, b) => {
      const da = a?.fechaCreacion ? Date.parse(a.fechaCreacion) : 0;
      const db = b?.fechaCreacion ? Date.parse(b.fechaCreacion) : 0;
      return db - da;
    });

  // Scroll a la tarea que vino desde navigate()
  useEffect(() => {
    if (!taskIdToOpen) return;

    // Esperar al render (importantísimo)
    setTimeout(() => {
      const element = taskRefs.current[taskIdToOpen];

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // Opcional: resaltar la tarjeta
        element.classList.add('highlight-task');
        setTimeout(() => element.classList.remove('highlight-task'), 2000);
      }
    }, 200); // pequeño delay para asegurar render
  }, [tasks, filteredTasks]);

  const isSacOrEepp = (task) =>
    task?.documenTypeBusinessAreaId === 2 ||
    task?.documenTypeBusinessAreaId === 11;

  const shouldShowPjudWarning = (task) =>
    task?.tipoEnvio === 2 && task?.templateId && !isSacOrEepp(task);

  const approveTaskWithDocumentFlow = async (task) => {
    if (!task?.id || !userId) return;

    try {
      setIsLoadingTaskApproved(true);

      const generatedHtml = await getTaskHtml(task.id);

      let htmlToUse = null;

      if (generatedHtml && typeof generatedHtml.data === 'string') {
        htmlToUse = generatedHtml.data;
      }

      if (!htmlToUse || htmlToUse.trim().length === 0) {
        console.warn(
          `No se pudo generar/obtener HTML para la tarea ${task.id}. No se publicará el documento.`
        );
        throw new Error(t('configTasks_error_documentFlow_fallback'));
      }

      const updateResult = await updateHtml(task.id, htmlToUse);
      if (!updateResult?.ok) {
        console.warn('No se pudo actualizar el HTML antes de publicar.');
        throw new Error(t('configTasks_error_documentFlow_fallback'));
      }

      const publishResult = await publish(task.id);
      if (!publishResult?.ok) {
        console.warn('No se pudo publicar el documento.');
        throw new Error(t('configTasks_error_documentFlow_fallback'));
      }

      if (task.documentConfigurationId && !isSacOrEepp(task)) {
        try {
          await assignDocumentToTask({
            documentConfigurationId: task.documentConfigurationId,
            taskId: task.id,
            userId,
            createdAt: new Date()
              .toLocaleString('sv-SE', { timeZone: 'America/Santiago' })
              .replace(' ', 'T'),
          });
        } catch (assignError) {
          console.warn(
            'No se pudo asociar el documento a la tarea:',
            assignError
          );
          throw new Error(t('configTasks_error_documentFlow_fallback'));
        }
      }

      const newStatusLabel = 'approved';
      const newStatusId = '1';

      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? {
                ...t,
                estado: newStatusLabel,
                estadoId: newStatusId,
                status: newStatusLabel,
              }
            : t
        )
      );

      await runChangeTaskInfo(task.id, newStatusId, userId);
      await fetchTasks();
    } catch (error) {
      console.error('Error aprobando tarea con flujo de documento:', error);
      setErrorTaskWithDocumentFlow(
        error instanceof Error
          ? error.message
          : t('configTasks_error_documentFlow_fallback')
      );
    } finally {
      setIsLoadingTaskApproved(false);
      setShowSendWarning(false);
      setTaskPendingApproval(null);
    }
  };

  const rejectTaskWithDocumentFlow = async (task) => {
    if (!task?.id || !userId) return;

    try {
      setIsLoadingTaskApproved(true);

      const generatedHtml = await getTaskHtml(task.id);

      let htmlToUse = null;

      if (generatedHtml && typeof generatedHtml.data === 'string') {
        htmlToUse = generatedHtml.data;
      }

      if (!htmlToUse || htmlToUse.trim().length === 0) {
        console.warn(
          `No se pudo generar/obtener HTML para la tarea ${task.id}. No se publicará el documento.`
        );
        throw new Error(t('configTasks_error_documentFlow_fallback'));
      }

      const updateResult = await updateHtml(task.id, htmlToUse);
      if (!updateResult?.ok) {
        console.warn(
          'No se pudo actualizar el HTML antes de subir el documento.'
        );
        throw new Error(t('configTasks_error_documentFlow_fallback'));
      }

      const publishResult = await publish(task.id);
      if (!publishResult?.ok) {
        console.warn('No se pudo publicar (subir) el documento.');
        throw new Error(t('configTasks_error_documentFlow_fallback'));
      }

      const newStatusLabel = 'rejected';
      const newStatusId = '4';

      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? {
                ...t,
                estado: newStatusLabel,
                estadoId: newStatusId,
                status: newStatusLabel,
              }
            : t
        )
      );

      await runChangeTaskInfo(task.id, newStatusId, userId);
      await fetchTasks();
    } catch (error) {
      console.error('Error rechazando tarea con flujo de documento:', error);
      setErrorTaskWithDocumentFlow(
        error instanceof Error
          ? error.message
          : t('configTasks_error_documentFlow_fallback')
      );
    } finally {
      setIsLoadingTaskApproved(false);
      setShowSendWarning(false);
      setTaskPendingApproval(null);
    }
  };

  const handleApproveClick = async (task) => {
    setErrorTaskWithDocumentFlow(null);
    const hasDocument = hasDocumentAttached(task);

    if (!hasDocument) {
      await handleApproveOrReject(true, task);
      return;
    }

    if (shouldShowPjudWarning(task)) {
      setTaskPendingApproval(task);
      setShowSendWarning(true);
      return;
    }

    await approveTaskWithDocumentFlow(task);
  };

  const handleRejectClick = async (task) => {
    setErrorTaskWithDocumentFlow(null);
    const hasDocument = hasDocumentAttached(task);

    if (!hasDocument) {
      await handleApproveOrReject(false, task);
      return;
    }

    await rejectTaskWithDocumentFlow(task);
  };

  const viewportHeight = useViewportHeight();

  let heightPercent = 60;

  if (viewportHeight < 600) {
    // Zoom 150%
    heightPercent = 45;
  } else if (viewportHeight < 700) {
    // Zoom 125%
    heightPercent = 54;
  } else if (viewportHeight < 800) {
    // Zoom 110%
    heightPercent = 64;
  } else if (viewportHeight < 900) {
    // Zoom 100%
    heightPercent = 73;
  } else if (viewportHeight < 1000) {
    // Zoom 90%
    heightPercent = 77;
  } else if (viewportHeight < 1100) {
    // Zoom 80%
    heightPercent = 83;
  }

  return (
    <div className='case-task-tab'>
      <div className='d-flex justify-content-end align-items-end my-3 px-4'>
        <Button
          label={t('configTasks_newTaskButton')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={() => {
            setTaskToEdit(null);
            setShowModal(true);
          }}
          disabled={!canCreateTask}
        />
      </div>
      <div className='px-4'>
        {loadingTasks && (
          <p className='font-light m-2'>{t('configTasks_loading')}</p>
        )}
      </div>

      {tasks?.length === 0 && !loadingTasks && (
        <p className='font-light text-center mt-4'>{t('configTasks_empty')}</p>
      )}
      {tasks?.length > 0 && !loadingTasks && (
        <div className='px-4'>
          <div className='row mt-4'>
            <div className='col-6'>
              <Input
                icon='search'
                placeholder={t('configTasks_searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className='col-3'>
              <Dropdown
                placeholder={t('configTasks_statusPlaceholder')}
                options={statusOptions}
                value={
                  statusOptions.find((opt) => opt.value === statusFilter) ||
                  statusOptions[0]
                }
                onChange={(opt) => setStatusFilter(opt?.value || 'todos')}
              />
            </div>
          </div>
        </div>
      )}

      {tasks?.length > 0 && filteredTasks.length === 0 && (
        <div className='px-4'>
          <p className='font-light m-2'>{t('configTasks_noMatches')}</p>
        </div>
      )}

      {/* Guard 409 del back (proceso de Renegociación pendiente / documentos que se
        confirman en esa vista): modal con el mensaje del back y "Ver proceso", que
        deep-linkea al proceso correcto. Cancelar/cerrar solo limpia el aviso. */}
      {naConflict && (
        <ConfirmModal
          Modal={Modal}
          show
          title={t('configTasks_na_process_title')}
          message={naConflict.message}
          confirmLabel={t('configTasks_na_review_task')}
          confirmVariant='primary'
          onConfirm={() => {
            const { checkId } = naConflict;
            setNaConflict(null);
            navigate(`/renegotiation/documents?checkId=${checkId}`);
          }}
          onClose={() => setNaConflict(null)}
        />
      )}

      {errorTaskWithDocumentFlow && (
        <FloatingAlert
          message={errorTaskWithDocumentFlow}
          type='danger'
          onClose={() => setErrorTaskWithDocumentFlow(null)}
        />
      )}
      {workflowError && (
        <FloatingAlert
          message={workflowError}
          type='danger'
          onClose={() => setWorkflowError(null)}
        />
      )}
      <div
        className='activity-inner-scroll p-4'
        style={{
          height: `${heightPercent}%`,
        }}
      >
        {filteredTasks.map((task) => (
          <TaskCard
            key={task.id}
            ref={(el) => (taskRefs.current[task.id] = el)}
            DesignSystemComponents={DesignSystemComponents}
            task={task}
            onView={() => {
              setTaskToEdit(task);
              setShowModal(true);
            }}
            onEdit={() => {
              setTaskToQuickEdit(task);
              setShowQuickModal(true);
            }}
            onSave={handleApproveOrReject}
            onAddComment={(taskId, text, files) =>
              handlePostComment(taskId, text, files)
            }
            onOpenAttachment={handleOpenAttachment}
            onOpenCommentDocument={handleOpenCommentDocument}
            onApprove={handleApproveClick}
            onReject={handleRejectClick}
            isLoadingTaskApproved={isLoadingTaskApproved}
            workflowInstance={
              workflowByTaskId[task.id]
                ? {
                    ...workflowByTaskId[task.id],
                    parsedStatus: parseWorkflowStatus(
                      workflowByTaskId[task.id].status
                    ),
                  }
                : null
            }
            onViewWorkflow={() => handleViewWorkflow(task)}
            isWorkflowFlowLoading={isWorkflowFlowLoading}
            canManageTask={canManageTask}
            canEditTask={canEditTask}
            canPublishComment={canPublishComment}
            canAttachFile={canAttachFile}
          />
        ))}
      </div>

      <ManageTaskModal
        DesignSystemComponents={DesignSystemComponents}
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setTaskToEdit(null);
        }}
        onSave={handleAddTask}
        taskTitles={taskTitleOptions}
        estados={localizedEstadoOptions}
        prioridades={prioridadOptions}
        causasIniciales={[caseData]}
        usuarios={allUserOptions}
        currentUserId={userId}
        defaultTaskType='causa'
        defaultCauseSearchMode='causa'
        disableCauseSearchMode
        disableCaseSelection
        useInitialCases
        toEdit={taskToEdit}
      />
      <EditTaskQuickModal
        DesignSystemComponents={DesignSystemComponents}
        show={showQuickModal}
        onClose={() => {
          setShowQuickModal(false);
          setTaskToQuickEdit(null);
        }}
        onSave={handleChangeTaskState}
        task={taskToQuickEdit}
        users={allUserOptions}
        statusOptions={localizedEstadoOptions}
      />
      {showAttachmentModal && (
        <Modal
          show={showAttachmentModal}
          onClose={() => setShowAttachmentModal(false)}
          title={t('configTasks_attachment_title')}
          width='1000px'
        >
          <div className='mt-4 p-3 border-top'>
            <p className='caption mb-2'>
              {selectedTaskForAttachment?.nameFile
                ? `${t('configTasks_attachment_fileNameLabel')} ${selectedTaskForAttachment.nameFile}`
                : t('configTasks_attachment_noFileName')}
            </p>

            {isLoadingHtml && (
              <p className='caption mb-2'>
                {t('configTasks_attachment_loadingGeneratedDoc')}
              </p>
            )}

            {!isLoadingHtml && htmlError && (
              <p className='caption text-danger mb-2'>
                {t('configTasks_attachment_htmlLoadError')}
              </p>
            )}

            {!isLoadingHtml && html && (
              <div className='mb-3'>
                <h6 className='mb-2'>
                  {t('configTasks_attachment_generatedDocTitle')}
                </h6>
                <Editor
                  apiKey={TinyMCE_ApiKey}
                  onInit={(evt, editor) => {
                    editorRef.current = editor;
                  }}
                  value={html}
                  onEditorChange={(c) => setHtml(c)}
                  init={{
                    height: '65vh',
                    menubar: true,
                    plugins:
                      'advlist autolink lists link charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime table code help wordcount',
                    toolbar:
                      'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist outdent indent | table | removeformat',
                    branding: false,
                    language: 'es',
                  }}
                />
              </div>
            )}

            <div className='d-flex justify-content-end mt-3'>
              <Button
                label={t('configTasks_attachment_saveButton')}
                size='sm'
                variant='primary'
                className='me-2'
                onClick={editTaskHtmlContent}
                disabled={
                  isUpdatingHtml ||
                  isLoadingHtml ||
                  selectedTaskForAttachment.estado === 'rejected' ||
                  selectedTaskForAttachment.estado === 'approved'
                }
              />

              <Button
                label={t('configTasks_attachment_closeButton')}
                size='sm'
                variant='secondary'
                onClick={() => setShowAttachmentModal(false)}
                disabled={isUpdatingHtml || isLoadingHtml}
              />
            </div>
          </div>
        </Modal>
      )}
      {showSendWarning && (
        <Modal
          show={showSendWarning}
          onClose={() => {
            if (!isLoadingTaskApproved) {
              setShowSendWarning(false);
              setTaskPendingApproval(null);
            }
          }}
          title={t('configTasks_sendWarning_title')}
          width='600px'
        >
          <div className='mt-4 p-3 border-top'>
            <p className='caption mb-3'>
              {t('configTasks_sendWarning_body_prefix')}{' '}
              <strong>{t('configTasks_sendWarning_body_highlight')}</strong>.{' '}
              {t('configTasks_sendWarning_body_suffix')}
            </p>

            <div className='d-flex justify-content-end'>
              <Button
                label={t('configTasks_sendWarning_cancel')}
                size='sm'
                variant='secondary'
                className='me-2'
                disabled={isLoadingTaskApproved}
                onClick={() => {
                  setShowSendWarning(false);
                  setTaskPendingApproval(null);
                }}
              />

              <Button
                label={
                  isLoadingTaskApproved
                    ? t('configTasks_sendWarning_sending')
                    : t('configTasks_sendWarning_sendDocument')
                }
                size='sm'
                variant='success'
                disabled={isLoadingTaskApproved}
                onClick={() => {
                  if (!taskPendingApproval) return;
                  approveTaskWithDocumentFlow(taskPendingApproval);
                }}
              />
            </div>
          </div>
        </Modal>
      )}
      {naIntercept && (
        <AdminNegotiationProcessModal
          DesignSystemComponents={DesignSystemComponents}
          mode='decide'
          checkId={naIntercept.checkId}
          clientLabel={naIntercept.clientLabel}
          initialDecision={naIntercept.selected ? 'approve' : 'reject'}
          onClose={() => setNaIntercept(null)}
          onCompleted={async () => {
            // El server ya sincronizó el estado de la tarea (dirección proceso→tarea);
            // acá solo cerramos el modal y refrescamos la lista.
            setNaIntercept(null);
            await fetchTasks();
          }}
        />
      )}
    </div>
  );
};

export default CaseTasks;
